import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';

import {
  CloudUpload,
  CloudUploadDark,
  DeviceSelected as UploadedIcon,
  InfoIcon,
} from 'src/assets/images';
import { compressImage } from 'src/helpers/jpeg-compression';
import ClassifyIdModal from '../classify-id-modal/classify-id-modal';
import toastMessageServiceSingleton from 'src/services/toast-message.service';
import TealiumEvents from 'src/helpers/tealium-events';
import MiniAppEvents from 'src/helpers/miniapp-events';
import useTechnicalErrorHandler from 'src/hooks/useTechnicalError';
import { OnboardingDocumentType } from 'src/helpers/constants';
import { getImageSizeFromBase64, matchesKVP } from 'src/helpers/functional-helpers';
import ShowIf from '../show-if/show-if';
import DocumentImageCaptureModal from '../document-image-capture-modal/document-image-capture-modal';
import { Colors } from 'src/common/colors';
import { ErrorCodeTooltip } from 'src/features/common/components/error-code-tooltip/error-code-tooltip';
import { Text } from 'src/common';
import { OnboardingDocumentTypeEnum } from 'src/features/onboarding/pages/upload-documents/upload-documents';

import './file-upload.scss';

const Subtext = ({ description, subtext, boldedText }) => {
  return (
    <>
      {!description ? (
        <p>{subtext}</p>
      ) : (
        <ul>
          {description.map((point) => (
            <li key={`point_${point.title}`}>
              <b>{point.title}</b>: {point.message}
            </li>
          ))}
        </ul>
      )}
      <b>{boldedText}</b>
    </>
  );
};

const FileUpload = (props) => {
  const [disabled, setDisabled] = useState(false);
  const [showIdModal, setShowIdModal] = useState(false);
  const [isWebcamUpload, setIsWebcamUpload] = useState(false);
  const maxSize = props.sizeLimit;
  const allowedFileTypes = props.allowedFileTypes;
  const handleTechnicalError = useTechnicalErrorHandler();
  const [showWebcamModal, setShowWebcamModal] = useState(false);
  const lightMode = props.lightMode || false;

  const idTypeList = [
    OnboardingDocumentType.OLD_GREEN_IDENTITY_DOCUMENT_TYPE,
    OnboardingDocumentType.NEW_IDENTITY_CARD_TYPE,
    OnboardingDocumentType.PASSPORT_TYPE,
  ];

  const isFileUploadIdType = props.documentType === OnboardingDocumentType.ID_TYPE;

  const isForeignNatonalDocument =
    props.documentType === OnboardingDocumentType.PASSPORT_TYPE ||
    props.documentType === OnboardingDocumentType.REFUGEE_LETTER ||
    props.documentType === OnboardingDocumentType.ASYLUM_SEEKER ||
    props.documentType === OnboardingDocumentType.WORK_PERMIT_TYPE ||
    props.documentType === OnboardingDocumentType.PERMANENT_RESIDENCY_TYPE;

  const checkForUploadedId = (docs) =>
    docs.length > 0 &&
    docs.some((document) => document.id_number === 'TBD' || document.document_name === 'TBD');

  const checkForPermitOrResidency = (docs) =>
    docs.length > 0 &&
    docs.some(
      (document) =>
        (document.documentType === OnboardingDocumentType.WORK_PERMIT_TYPE ||
          document.documentType === OnboardingDocumentType.PERMANENT_RESIDENCY_TYPE) &&
        document.idNumber === 'TBD',
    );

  const removeUploadedFile = (fileToRemove) => {
    const newDocs = props.currentDocuments.documents.filter(
      (file) => !Object.is(file, fileToRemove),
    );
    props.setDeletedDocs(fileToRemove.content_document_id);
    props.setCurrentDocuments({
      documents: newDocs,
    });
  };

  const base64ToFile = (base64, filename, mime) => {
    let base64String = atob(base64);
    let base64StrLength = base64String.length;
    let u8Array = new Uint8Array(base64StrLength);

    while (base64StrLength--) {
      u8Array[base64StrLength] = base64String.charCodeAt(base64StrLength);
    }

    return new File([u8Array], filename, { type: mime });
  };

  const getFileType = (base64Data) => {
    //Get messageType
    var fileType = null;
    //check for JPEG ['application/pdf', 'image/jpeg']
    const regex = new RegExp('(?=^(?:.{4})*$)^JVBER[a-zA-Z0-9+/]*={0,3}$');
    if (base64Data.charAt(0) === '/') {
      fileType = { ext: '.jpg', mime: 'image/jpeg' };
    } else if (regex.test(base64Data)) {
      //Check for pdf
      fileType = { ext: '.pdf', mime: 'application/pdf' };
    }
    return fileType;
  };

  const getBase64DataOnly = (urlEncodedString) =>
    urlEncodedString ? urlEncodedString.split(',')[1] : '';

  const compressOptions = {
    idDocument: { maxSizeMB: 1, maxWidthOrHeight: 640 },
    default: { maxSizeMB: 3 },
  };

  const compressIfJpegCached = (files) =>
    files.map((file) => {
      if (file.type === 'image/jpeg') {
        return compressImage(
          file,
          isFileUploadIdType ? compressOptions.idDocument : compressOptions.default,
        );
      } else {
        return file;
      }
    });

  const onDrop = (acceptedFiles) => {
    if (props.onDocumentUpload) {
      props.onDocumentUpload();
    }
    if (acceptedFiles.length > 0) {
      Promise.all(compressIfJpegCached(acceptedFiles)).then((newFiles) => {
        Promise.all(newFiles.map(readCurrentFile))
          .then((uploadedFiles) => {
            const newDocuments = [];
            if (props.currentDocuments.documents?.length > 0) {
              props.currentDocuments.documents.map((element) => newDocuments.push(element));
            }
            uploadedFiles.forEach((element) => {
              newDocuments.push({
                document_body: element.fileData,
                document_type: props.documentType,
                document_name: element.fileName,
                id_number: isFileUploadIdType || isForeignNatonalDocument ? 'TBD' : undefined,
                unconfirmed: isFileUploadIdType || isForeignNatonalDocument,
              });
            });

            props.setCurrentDocuments({
              documents: newDocuments,
            });

            if (checkForUploadedId(newDocuments)) {
              if (props.onShowIdModal) {
                props.onShowIdModal();
              }
              setShowIdModal(true);
              return;
            }

            if (checkForPermitOrResidency(newDocuments)) {
              setShowIdModal(true);
              return;
            }
          })
          .catch((error) => {
            console.error(error);
          });
      });
    }
  };

  const readCurrentFile = (acceptedFile) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve({ fileData: getBase64DataOnly(reader.result), fileName: acceptedFile.name });
      };
      reader.readAsDataURL(acceptedFile);
    });
  };

  const getUpdatedDoc = (oldFileName, newId, newIdType, documentType, newFileName) => {
    const updatedDoc = props.currentDocuments.documents.filter((file) => {
      if (documentType === OnboardingDocumentType.ID_TYPE) {
        return matchesKVP(file, {
          document_name: oldFileName,
          id_number: 'TBD',
          document_type: OnboardingDocumentType.ID_TYPE,
        });
      } else {
        return matchesKVP(file, {
          document_name: oldFileName,
          document_type: documentType,
        });
      }
    })[0];
    return {
      ...updatedDoc,
      unconfirmed: false,
      id_number: newId,
      document_name: newFileName ? newFileName : oldFileName,
      document_type: newIdType ? newIdType : documentType,
    };
  };

  const updateIdFiles = (oldFileNames, id, idType, documentType, newFileName) => {
    const newDocs = oldFileNames.reduce((accum, oldFileName) => {
      const newDocList = [];
      newDocList.push(getUpdatedDoc(oldFileName, id, idType, documentType, newFileName));
      const oldList = accum.filter((file) => {
        if (file.documentType === OnboardingDocumentType.ID_TYPE) {
          return !matchesKVP(file, {
            document_name: oldFileName,
            document_type: OnboardingDocumentType.ID_TYPE,
            id_number: 'TBD',
          });
        } else {
          return !matchesKVP(file, {
            document_name: oldFileName,
            document_type: documentType,
          });
        }
      });
      accum = oldList.concat(newDocList);
      return accum;
    }, props.currentDocuments.documents);

    props.setCurrentDocuments({
      documents: newDocs,
    });
  };

  const getFileNames = () => {
    const fileNames = [];
    if (props.currentDocuments.documents?.length > 0) {
      props.currentDocuments.documents.forEach((element) => {
        if (element.id_number === 'TBD' || element.document_name === 'TBD') {
          fileNames.push(element.document_name);
        }
      });
    }
    return fileNames;
  };

  const hasUploadedFile = () =>
    props.currentDocuments?.documents?.findIndex(
      (file) =>
        file.document_type === props.documentType ||
        (isFileUploadIdType && idTypeList.includes(file.document_type)),
    ) >= 0;

  const removeMultipleFiles = () => {
    const newDocs = props.currentDocuments?.documents?.filter(
      (doc) => !doc.unconfirmed && doc.documentName !== 'TBD',
    );
    props.setCurrentDocuments({
      documents: newDocs,
    });
  };

  const onDropRejected = (fileRejections) => {
    if (fileRejections[0].errors[0].code === 'file-invalid-type') {
      const errorMessage =
        props.unsupportedFileTypeErrorMessage ||
        `File upload failed: Incorrect file type, only ${allowedFileTypes} supported.`;
      toastMessageServiceSingleton.addError(errorMessage, null, 'N/A', true, null, true);
      if (props.onUploadError) {
        props.onUploadError(errorMessage);
      }
    } else if (fileRejections[0].errors[0].code === 'file-too-large') {
      const errorMessage = `File upload failed: Maximum file size of ${
        maxSize > 1000000 ? `${maxSize / 1024000}MB` : `${maxSize / 1024}KB`
      } exceeded.`;
      toastMessageServiceSingleton.addError(errorMessage, null, 'N/A', true, null, true);
      if (props.onUploadError) {
        props.onUploadError(errorMessage);
      }
    } else if (fileRejections[0].errors[0].code === 'dxl-upload-error') {
      TealiumEvents.dxlErrorUploadMiniApp(fileRejections[0].errors[0].message, 'document upload');
      handleTechnicalError(fileRejections[0].errors[0].message, true, true);
    } else {
      const errorMessage = fileRejections[0].errors[0].message;
      toastMessageServiceSingleton.addError(errorMessage, null, 'N/A', false, null, true);
      if (props.onUploadError) {
        props.onUploadError(errorMessage);
      }
    }
  };

  const getUploadedIds = () =>
    (props?.currentDocuments?.documents || []).filter(
      (doc) =>
        doc.document_type === OnboardingDocumentTypeEnum.ID_BOOK ||
        doc.document_type === OnboardingDocumentTypeEnum.ID_CARD,
    );

  const { isDragActive, getRootProps, getInputProps, isDragReject, open } = useDropzone({
    onDrop,
    onDropRejected,
    accept: allowedFileTypes,
    minSize: 0,
    maxSize,
    noClick: !props.inMiniProgram,
    disabled: disabled,
  });

  const miniappOnClick = () => {
    MiniAppEvents.listenForMessage((observer) => {
      const message = MiniAppEvents.readMessage();
      if (message.type === props.documentType) {
        observer.disconnect();
        if (message.error) {
          //Occurs if DXL upload fails on mini-app side
          onDropRejected([
            { errors: [{ code: message.errorType, message: message.errorMessage }] },
          ]);
        } else {
          if (message.url) {
            let documentCount = 0;

            if (props.currentDocuments.documents?.length > 0) {
              documentCount = props.currentDocuments.documents.filter(
                (item) =>
                  item.document_type === message.type ||
                  (message.type === OnboardingDocumentType.ID_TYPE &&
                    idTypeList.includes(item.document_type)),
              ).length;
            }

            message.name = message.type + (documentCount > 0 ? ` (${documentCount})` : '');
            MiniAppEvents.downloadBase64FileURL(message.url)
              .then((res) => {
                const base64Data = res;

                const fileType = getFileType(base64Data);

                if (fileType !== null && allowedFileTypes.includes(fileType.mime)) {
                  //If message type is one of the allowed types.
                  const messageNameAndType = message.name.split('.')[0] + fileType.ext;
                  const file = base64ToFile(base64Data, messageNameAndType, fileType.mime);
                  onDrop([file]);
                } else {
                  //Reject file with invalid type
                  onDropRejected([{ errors: [{ code: 'file-invalid-type' }] }]);
                }
              })
              .catch((error) => {
                //Occurs if DXL download fails on portal side
                TealiumEvents.dxlErrorDownloadMiniApp(error, 'document upload');
                handleTechnicalError(error, true, true);
              });
          }
        }
      }
    });
    MiniAppEvents.sendMessage({
      messageType: props.documentType,
      maxSize: maxSize,
    });
  };

  React.useEffect(() => {
    setDisabled(props.maxFiles && getUploadedIds().length >= props.maxFiles);
  }, [props.currentDocuments, props.maxFiles]);

  const openCameraClicked = (event) => {
    TealiumEvents.docsOpenCameraButton(props.customerType, props.documentType);
    setShowWebcamModal(true);
    event.stopPropagation();
  };

  if (props.inMiniProgram) {
    return (
      <div
        className={`has-margin-x-6 ${props.isPending && !props.hints ? 'disabled' : ''}`}
        key={props.key}
      >
        <ClassifyIdModal
          show={showIdModal}
          fileNames={getFileNames()}
          ids={props.idNumbers || props.passportNumbers}
          isForeignNatonalDocument={isForeignNatonalDocument}
          documentTypeName={props.titleOriginal}
          documentType={props.documentType}
          onHide={() => setShowIdModal(false)}
          onClose={(fileNames) => {
            removeMultipleFiles(fileNames);
          }}
          onSubmission={(fileNames, id, idType) => {
            updateIdFiles(fileNames, id, idType);
          }}
          disabled={false}
          automationIdPrefix={props.automationIdPrefix}
        />
        <div {...getRootProps()} onClick={() => miniappOnClick()}>
          <div
            className={`${disabled ? 'disabled !opacity-100' : ''} file-upload
            ${props.currentDocuments.documents?.length > 0 ? '' : 'center-box'} has-text-center ${
              hasUploadedFile() ? 'upload-success' : ''
            }`}
          >
            {hasUploadedFile() && <img className="checkmark" src={UploadedIcon} />}
            <div className=" has-padding-x-6 has-padding-y-6 has-padding-bottom-3">
              <p className="upload-title has-padding-left-6">{props.title}</p>
              <div
                className={`columns has-margin-6 is-mobile has-text-centered inner-upload-container`}
              >
                <div className={hasUploadedFile() ? 'column is-three-fifths' : ''}>
                  <div className="columns has-padding-top-6 resizable-div">
                    {props.currentDocuments.documents?.length > 0 &&
                      props.currentDocuments.documents.map((file, index) => {
                        if (
                          file.document_type === props.documentType ||
                          (isFileUploadIdType && idTypeList.includes(file.document_type))
                        ) {
                          return (
                            <>
                              <div
                                className="uploaded-file-bubble is-one-fifth has-margin-right-6"
                                key={`${file.document_name}_${file.document_type}_${index}_${
                                  file.id_number ? file.id_number : ''
                                }`}
                              >
                                <p
                                  className="uploaded-doc-name"
                                  automationid={`${props.automationIdPrefix}_Paragraph_Uploaded_Document_Name`}
                                >
                                  {file.document_name}
                                </p>
                                <span
                                  className={`!pointer-events-auto close-button ${
                                    isFileUploadIdType ? 'id-center' : 'non-id'
                                  }`}
                                  onClick={(e) => {
                                    removeUploadedFile(file);
                                    e.stopPropagation();
                                  }}
                                  automationid={`${props.automationIdPrefix}_Span_Delete_Uploaded_Document`}
                                ></span>
                                <p className="id-subtext">{file.id_number}</p>
                              </div>
                            </>
                          );
                        }
                      })}
                  </div>
                </div>
                <div
                  className={`padding-drag-area has-text-centered`}
                  automationid={`${props.automationIdPrefix}_Upload_Text`}
                >
                  <img className="cloud-image" src={CloudUpload}></img>
                  <span id="grey-drag-text"> Drag and drop file here or </span>
                  <span id="upload-text">Upload</span>
                  <span id="mobile-text"> Upload</span>
                </div>
              </div>

              <span className="upload-subtext has-padding-left-6 is-hidden-mobile">
                <Subtext
                  description={props.descriptionPoints}
                  subtext={props.subtext}
                  boldedText={props.boldedText}
                />
              </span>
              <span className="upload-subtext is-pulled-right has-padding-right-6">
                {props.fileLimitText}
              </span>
            </div>
          </div>
        </div>
        <div className="upload-subtext has-margin-left-6 has-mobile-margin-left-0 has-mobile-padding-x-6 is-hidden-tablet fullwidth has-padding-top-7 has-padding-bottom-5">
          <Subtext
            description={props.descriptionPoints}
            subtext={props.subtext}
            boldedText={props.boldedText}
          />
        </div>
        {props.isPending && props.hints && (
          <ErrorCodeTooltip lightMode={false} isUpload={true} content={props.hints}>
            <Text
              gap={'7px'}
              display={'flex'}
              fontSize={'12px'}
              margin={'21px 0 0 0'}
              icon={InfoIcon}
              cursor={'pointer'}
              zIndex={100}
              color={Colors.LightTeal}
              textDecoration={'underline'}
            >
              {'Why am I seeing this?'}
            </Text>
          </ErrorCodeTooltip>
        )}
      </div>
    );
  }

  return (
    <div
      className={`has-margin-x-6 ${props.isPending && !props.hints ? 'disabled' : ''}`}
      key={props.key}
    >
      <ClassifyIdModal
        show={showIdModal}
        fileNames={getFileNames()}
        ids={props.idNumbers || props.passportNumbers}
        isForeignNatonalDocument={isForeignNatonalDocument}
        documentTypeName={props.titleOriginal}
        documentType={props.documentType}
        onHide={() => {
          setShowIdModal(false);
          setIsWebcamUpload(false);
        }}
        onClose={(fileNames) => removeMultipleFiles(fileNames)}
        onSubmission={(oldFileNames, id, idType, newFileName) => {
          updateIdFiles(oldFileNames, id, idType, props.documentType, newFileName);
        }}
        isWebcamUpload={isWebcamUpload}
        disabled={false}
        automationIdPrefix={props.automationIdPrefix}
      />
      <DocumentImageCaptureModal
        show={showWebcamModal}
        onHide={() => setShowWebcamModal(false)}
        onImageCapture={(imgSrc) => {
          // TODO: move base64 conversion into image-capture component
          const base64Data = imgSrc ? imgSrc.split(',')[1] : '';
          const fileType = getFileType(base64Data);

          if (getImageSizeFromBase64(base64Data) > maxSize) {
            const errorMessage = `File upload failed: Maximum file size of ${
              maxSize > 1000000 ? `${maxSize / 1024000}MB` : `${maxSize / 1024}KB`
            } exceeded.`;
            toastMessageServiceSingleton.addError(errorMessage, null, 'N/A', false, null, true);
            return;
          }

          if (fileType !== null && allowedFileTypes.includes(fileType.mime)) {
            //If message type is one of the allowed types.
            const file = base64ToFile(base64Data, 'TBD', fileType.mime);
            setIsWebcamUpload(true);
            onDrop([file]);
            TealiumEvents.docsCaptureImage(props.customerType);
          } else {
            //Reject file with invalid type
            onDropRejected([{ errors: [{ code: 'file-invalid-type' }] }]);
          }
        }}
        refreshTagging={() => {
          TealiumEvents.docsRefreshCamera(props.customerType);
        }}
        switchTagging={() => {
          TealiumEvents.docsSwitchCamera(props.customerType);
        }}
        automationIdPrefix="Onboarding_Create_Account_Your_Details"
        customerType={props.customerType}
      />

      <div
        {...getRootProps()}
        onClick={(event) => {
          event.stopPropagation();
          open();
        }}
      >
        <input
          {...getInputProps()}
          automationid={`${props.automationIdPrefix}_Input_Upload_Document`}
        />
        <div
          className={`${disabled ? 'disabled !opacity-100' : ''} file-upload ${
            lightMode ? 'light-file-upload' : ''
          }
            ${isDragActive && !isDragReject ? 'valid-file-drag' : ''} ${
            props.currentDocuments.documents?.length > 0 ? '' : 'center-box'
          } has-text-center ${isDragActive ? 'drag-over' : ''} ${
            isDragReject ? 'drag-reject' : ''
          } ${!isDragReject && hasUploadedFile() ? 'upload-success' : ''}`}
        >
          {!isDragReject && hasUploadedFile() && <img className="checkmark" src={UploadedIcon} />}
          <div className=" has-padding-6 min-height">
            <p className="upload-title has-padding-left-6">{props.title}</p>
            {!isDragActive && (
              <div
                className={`columns has-margin-6 is-mobile has-text-centered inner-upload-container ${
                  isDragActive ? 'invisible' : ''
                }`}
              >
                <div className={hasUploadedFile() ? 'column is-three-fifths' : ''}>
                  <div className="columns has-padding-top-6 resizable-div">
                    {props.currentDocuments.documents?.length > 0 &&
                      props.currentDocuments.documents.map((file, index) => {
                        if (
                          file.document_type === props.documentType ||
                          (isFileUploadIdType && idTypeList.includes(file.document_type))
                        ) {
                          return (
                            <>
                              <div
                                className={`uploaded-file-bubble is-one-fifth has-margin-right-6 ${
                                  lightMode ? '' : 'bubble-shadow'
                                }`}
                                key={`${file.document_name}_${file.document_type}_${index}_${
                                  file.id_number ? file.id_number : ''
                                }`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <p
                                  className="uploaded-doc-name"
                                  automationid={`${props.automationIdPrefix}_Paragraph_Uploaded_Document_Name`}
                                >
                                  {file.document_name}
                                </p>
                                <span
                                  className={`!pointer-events-auto  close-button ${
                                    isFileUploadIdType ? 'id-center' : 'non-id'
                                  }`}
                                  onClick={(e) => {
                                    removeUploadedFile(file);
                                    e.stopPropagation();
                                  }}
                                  automationid={`${props.automationIdPrefix}_Span_Delete_Uploaded_Document`}
                                ></span>
                                <p className="id-subtext">{file.id_number}</p>
                              </div>
                            </>
                          );
                        }
                      })}
                  </div>
                </div>
                <div
                  className={`padding-drag-area has-text-centered`}
                  automationid={`${props.automationIdPrefix}_Upload_Text`}
                >
                  <img
                    className="cloud-image"
                    src={lightMode ? CloudUploadDark : CloudUpload}
                  ></img>
                  <span id="grey-drag-text"> Drag and drop file here or </span>
                  <span id="upload-text">Upload</span>
                  <span id="grey-drag-text">or </span>
                  <span id="upload-text" onClick={openCameraClicked}>
                    Open camera
                  </span>
                  <span id="mobile-text"> Upload</span>
                </div>
              </div>
            )}
            {isDragActive && (
              <div
                className={`columns has-margin-6 is-mobile has-text-centered inner-upload-container height-limit ${
                  isDragActive ? 'padding-drag-area-active' : 'invisible'
                }`}
              >
                <div className="column has-text-centered">
                  {isDragActive && !isDragReject && (
                    <img className="cloud-image" src={CloudUpload}></img>
                  )}
                  {isDragReject && (
                    <div className="has-text-centered">
                      <p className="reject-text">
                        File type not accepted. Please click to retry again
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="columns">
              <div className="column">
                <p className="upload-subtext has-padding-x-6 is-hidden-mobile">
                  <Subtext
                    description={props.descriptionPoints}
                    subtext={props.subtext}
                    boldedText={props.boldedText}
                  />
                </p>
              </div>
              <ShowIf condition={!!props.fileLimitText}>
                <div className="column is-3">
                  <p className="upload-subtext is-pulled-right has-padding-right-6">
                    {props.fileLimitText}
                  </p>
                </div>
              </ShowIf>
            </div>
          </div>
        </div>
      </div>
      <div
        className={`upload-subtext has-margin-left-6 has-mobile-margin-left-0 has-mobile-padding-x-6 is-hidden-tablet fullwidth has-padding-top-7 has-padding-bottom-5 ${
          lightMode ? 'light-mode-text' : ''
        }`}
      >
        <Subtext
          description={props.descriptionPoints}
          subtext={props.subtext}
          boldedText={props.boldedText}
        />
      </div>
      {props.isPending && props.hints && (
        <ErrorCodeTooltip lightMode={false} isUpload={true} content={props.hints}>
          <Text
            gap={'7px'}
            display={'flex'}
            fontSize={'12px'}
            margin={'21px 0 0 0'}
            icon={InfoIcon}
            cursor={'pointer'}
            zIndex={100}
            color={Colors.LightTeal}
            textDecoration={'underline'}
          >
            {'Why am I seeing this?'}
          </Text>
        </ErrorCodeTooltip>
      )}
    </div>
  );
};

export default FileUpload;
